import { useState, type CSSProperties, type FormEvent } from "react";
import { useAuthSession } from "../auth/useAuthSession.ts";
import { Brand } from "../components/common/Brand.tsx";
import { flushPendingReferral } from "../conversion/captureReferralOnLand.ts";

type Mode = "signin" | "signup";

export function authFailureMessage(error: unknown): string {
  if (error instanceof DOMException && error.name === "AbortError") {
    return "The server took too long to respond. Please try again.";
  }
  if (error instanceof Error && error.message.trim().length > 0) {
    return error.message;
  }
  return "Sign-in could not be completed. Please try again.";
}

const wrap: CSSProperties = {
  minHeight: "var(--vh-full)",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  padding: "var(--sz-space-7)",
  background: "var(--sz-bg-base)",
};

const card: CSSProperties = {
  width: "100%",
  maxWidth: "var(--sz-card-centered-max)",
  background: "var(--sz-bg-surface)",
  border: "1px solid var(--sz-border-default)",
  borderRadius: "var(--sz-radius-lg)",
  padding: "40px 36px",
  display: "flex",
  flexDirection: "column",
  gap: "var(--sz-space-5)",
};

const eyebrow: CSSProperties = {
  fontFamily: "var(--sz-font-mono)",
  fontSize: "var(--sz-mono-sm)",
  letterSpacing: "3px",
  textTransform: "uppercase",
  color: "var(--sz-amber-text)",
};

const title: CSSProperties = {
  fontFamily: "var(--sz-font-head)",
  fontSize: "26px",
  fontWeight: 700,
  color: "var(--sz-text-primary)",
};

const fieldGroup: CSSProperties = {
  display: "flex",
  flexDirection: "column",
  gap: "var(--sz-space-2)",
};

const label: CSSProperties = {
  fontFamily: "var(--sz-font-mono)",
  fontSize: "var(--sz-mono-xs)",
  letterSpacing: "2px",
  textTransform: "uppercase",
  color: "var(--sz-text-muted)",
};

const input: CSSProperties = {
  background: "var(--sz-bg-surface-2)",
  border: "1px solid var(--sz-border-default)",
  borderRadius: "var(--sz-radius-md)",
  color: "var(--sz-text-primary)",
  fontFamily: "var(--sz-font-mono)",
  fontSize: "var(--sz-mono-lg)",
  padding: "12px 16px",
  outline: "none",
  width: "100%",
};

const primaryBtn: CSSProperties = {
  background: "var(--sz-amber)",
  color: "var(--sz-text-inverse)",
  border: "none",
  padding: "14px 20px",
  fontFamily: "var(--sz-font-body)",
  fontSize: "var(--sz-body-sm)",
  fontWeight: 500,
  letterSpacing: "1px",
  textTransform: "uppercase",
  borderRadius: "var(--sz-radius-md)",
};

const toggleBtn: CSSProperties = {
  fontFamily: "var(--sz-font-body)",
  fontSize: "var(--sz-body-sm)",
  color: "var(--sz-amber-text)",
  textAlign: "center",
  padding: "8px 0",
};

const message = (kind: "error" | "info"): CSSProperties => ({
  fontFamily: "var(--sz-font-body)",
  fontSize: "var(--sz-body-sm)",
  color: kind === "error" ? "var(--sz-danger-text)" : "var(--sz-success-text)",
  background: kind === "error" ? "var(--sz-danger-dim)" : "var(--sz-success-dim)",
  border: `1px solid ${kind === "error" ? "var(--sz-danger)" : "var(--sz-success)"}`,
  padding: "10px 14px",
  borderRadius: "var(--sz-radius-md)",
});

const footnote: CSSProperties = {
  fontFamily: "var(--sz-font-mono)",
  fontSize: "var(--sz-mono-sm)",
  letterSpacing: "1px",
  color: "var(--sz-text-muted)",
  textAlign: "center",
  marginTop: "var(--sz-space-3)",
};

export function AuthPage(): JSX.Element {
  const { signIn, signUp } = useAuthSession();
  const [mode, setMode] = useState<Mode>("signin");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [info, setInfo] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  function toggleMode(): void {
    setMode((m) => (m === "signin" ? "signup" : "signin"));
    setError(null);
    setInfo(null);
  }

  async function handleSubmit(e: FormEvent<HTMLFormElement>): Promise<void> {
    e.preventDefault();
    setError(null);
    setInfo(null);
    setBusy(true);
    try {
      if (mode === "signin") {
        const { error: err } = await signIn(email, password);
        if (err) setError(err.message);
      } else {
        const { error: err, needsConfirmation } = await signUp(email, password);
        if (err) {
          setError(err.message);
        } else {
          // Phase JB.3b — if a ?ref code was captured pre-signup, attempt to
          // attribute it now. The DB trigger on profiles INSERT actually
          // creates the referral row; this just persists the captured code
          // server-side. Non-blocking — never breaks signup.
          void flushPendingReferral();
          if (needsConfirmation) {
            setInfo("Account created. Check your inbox to confirm your email before signing in.");
          }
        }
        // If a session was returned, AuthGate will route us to /home.
      }
    } catch (submitError) {
      setError(authFailureMessage(submitError));
    } finally {
      setBusy(false);
    }
  }

  const isSignIn = mode === "signin";
  return (
    <div style={wrap}>
      <form style={card} onSubmit={handleSubmit} noValidate>
        <Brand />
        <span style={eyebrow}>{isSignIn ? "Sign in" : "Create account"}</span>
        <h1 style={title}>{isSignIn ? "Welcome back" : "Welcome to STRIKZA"}</h1>

        {error && <div style={message("error")}>{error}</div>}
        {info && <div style={message("info")}>{info}</div>}

        <div style={fieldGroup}>
          <label htmlFor="email" style={label}>
            Email
          </label>
          <input
            id="email"
            type="email"
            autoComplete="email"
            required
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            style={input}
          />
        </div>

        <div style={fieldGroup}>
          <label htmlFor="password" style={label}>
            Password
          </label>
          <input
            id="password"
            type="password"
            autoComplete={isSignIn ? "current-password" : "new-password"}
            required
            minLength={8}
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            style={input}
          />
        </div>

        <button type="submit" style={primaryBtn} disabled={busy}>
          {busy ? "..." : isSignIn ? "Sign in" : "Create account"}
        </button>

        <button type="button" style={toggleBtn} onClick={toggleMode}>
          {isSignIn ? "New to STRIKZA? Create an account" : "Already have an account? Sign in"}
        </button>

        <p style={footnote}>Phase 1A uses email and password only. Google OAuth comes later.</p>
      </form>
    </div>
  );
}
